/** CMDBuild schema administration and the optional demo backups. */

import { useCallback, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { useToast } from '../../components/Toast';
import {
  cleanupDeprecatedSchema,
  provisionCraWatchSchema,
  wipeCraWatchData,
} from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { countLabel } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import { IMPORT_KLASS_LABELS } from '../../lib/types';
import type {
  SchemaCleanupResponse,
  SchemaProvisionResponse,
  SchemaWipeResponse,
} from '../../lib/types';
import OperationCard from '../admin/OperationCard';
import SchemaFileCard from '../admin/SchemaFileCard';
import DemoBackupsCard from './DemoBackupsCard';

/** German label for a known class; unknown classes keep their raw name. */
function klassLabel(klass: string): string {
  const labels: Readonly<Record<string, string | undefined>> = IMPORT_KLASS_LABELS;
  return labels[klass] ?? klass;
}

/** Typed exactly like this before the wipe button arms. */
const WIPE_CONFIRM_WORD = 'LÖSCHEN';

const NO_SCHEMA_ADMIN = missingPermissionTitle('schema.admin');
const NO_IMPORT_WRITE = missingPermissionTitle('import.write');

export function SystemDataModelPage(): ReactNode {
  const { has, user } = useAuth();
  const toast = useToast();
  const canSchema = has('schema.admin');
  const canImport = has('import.write');

  const [cleanupConfirmed, setCleanupConfirmed] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState('');

  const report = useCallback(
    (message: string, errors: readonly string[]): void => {
      if (errors.length > 0) {
        toast.error(`${message} ${countLabel(errors.length, 'Fehler', 'Fehler')}, Details in der Karte.`);
      } else {
        toast.success(message);
      }
    },
    [toast],
  );

  const wipeArmed = wipeConfirm.trim().toUpperCase() === WIPE_CONFIRM_WORD;
  const demoBackupsDisabledReason = !canSchema
    ? NO_SCHEMA_ADMIN
    : !canImport
      ? NO_IMPORT_WRITE
      : !user?.demo_reset_enabled
        ? 'Demo-Backups sind in dieser Installation deaktiviert.'
        : null;

  return (
    <>
      {user?.app_variant === 'demo' ? (
        <div className="card">
          <div className="card-head">
            <h2>IT-SA Demo</h2>
          </div>
          <div className="card-body">
            <DemoBackupsCard disabledReason={demoBackupsDisabledReason} />
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2>CMDBuild-Schema</h2>
        </div>
        <div className="card-body">
          {!canSchema ? (
            <Banner kind="warn">
              Schema-Operationen sind für Ihr Konto gesperrt: es fehlt die Berechtigung{' '}
              <code className="mono">schema.admin</code>.
            </Banner>
          ) : null}
          <div className="ops-grid">
            <OperationCard<SchemaProvisionResponse>
              title="Schema anlegen / ergänzen"
              description="Legt fehlende Klassen, Attribute, Domains und Lookups des AssetRadar-Datenmodells an. Vorhandenes bleibt unverändert."
              buttonLabel="Schema provisionieren"
              runningLabel="Provisioniere…"
              disabledReason={canSchema ? null : NO_SCHEMA_ADMIN}
              run={provisionCraWatchSchema}
              errorsOf={(response) => response.result.errors}
              onDone={(response, errors) =>
                report(
                  `Schema geprüft: ${response.result.created_classes.length} Klassen und ${response.result.created_attributes.length} Attribute neu.`,
                  errors,
                )
              }
              summarise={(response) => {
                const result = response.result;
                return (
                  <>
                    Neu: {result.created_classes.length} Klassen,{' '}
                    {result.created_attributes.length} Attribute, {result.created_domains.length}{' '}
                    Domains, {result.created_lookups.length} Lookups. Bereits vorhanden:{' '}
                    {result.skipped_classes.length} Klassen, {result.skipped_attributes.length}{' '}
                    Attribute, {result.skipped_domains.length} Domains,{' '}
                    {result.skipped_lookups.length} Lookups.
                  </>
                );
              }}
            />

            <SchemaFileCard disabledReason={canSchema ? null : NO_SCHEMA_ADMIN} onDone={report} />

            <OperationCard<SchemaCleanupResponse>
              title="Veraltete Schema-Objekte entfernen"
              description="Löscht Klassen, Attribute und Domains aus früheren Modellständen samt der darin verbleibenden Karten. Nicht rückgängig machbar."
              buttonLabel="Aufräumen starten"
              runningLabel="Räume auf…"
              destructive
              disabledReason={
                !canSchema
                  ? NO_SCHEMA_ADMIN
                  : cleanupConfirmed
                    ? null
                    : 'Bitte zuerst bestätigen, dass veraltete Objekte samt Karten entfernt werden dürfen.'
              }
              run={cleanupDeprecatedSchema}
              errorsOf={(response) => response.result.errors}
              onDone={(response, errors) => {
                setCleanupConfirmed(false);
                report(
                  `Aufräumen abgeschlossen: ${response.result.removed.length} Objekte entfernt.`,
                  errors,
                );
              }}
              summarise={(response) => (
                <>
                  {countLabel(response.result.removed.length, 'Objekt entfernt', 'Objekte entfernt')}.
                  Details (Karten, Attribute, Domains, Klassen) im JSON.
                </>
              )}
            >
              <label className="check">
                <input
                  type="checkbox"
                  checked={cleanupConfirmed}
                  onChange={(event) => setCleanupConfirmed(event.target.checked)}
                  disabled={!canSchema}
                />
                Ja, veraltete Objekte samt enthaltener Karten entfernen
              </label>
            </OperationCard>

            <OperationCard<SchemaWipeResponse>
              title="Alle AssetRadar-Daten löschen"
              description="Löscht sämtliche Karten der AssetRadar-Klassen in CMDBuild: Kunden, Produkte, Komponenten, CVEs, Events und Benachrichtigungen. Das Schema bleibt bestehen. Nicht rückgängig machbar."
              buttonLabel={`Endgültig löschen (${WIPE_CONFIRM_WORD})`}
              runningLabel="Lösche…"
              destructive
              disabledReason={
                !canSchema
                  ? NO_SCHEMA_ADMIN
                  : wipeArmed
                    ? null
                    : `Bitte zur Bestätigung „${WIPE_CONFIRM_WORD}“ eingeben.`
              }
              run={wipeCraWatchData}
              errorsOf={(response) => response.result.errors}
              onDone={(response, errors) => {
                setWipeConfirm('');
                const total = Object.values(response.result.deleted).reduce(
                  (sum, count) => sum + count,
                  0,
                );
                report(`Datenbestand gelöscht: ${total} Karten entfernt.`, errors);
              }}
              summarise={(response) => {
                const entries = Object.entries(response.result.deleted).filter(
                  ([, count]) => count > 0,
                );
                const total = entries.reduce((sum, [, count]) => sum + count, 0);
                return (
                  <>
                    {countLabel(total, 'Karte gelöscht', 'Karten gelöscht')}
                    {entries.length > 0
                      ? `: ${entries.map(([klass, count]) => `${klassLabel(klass)} ${count}`).join(', ')}`
                      : ''}
                  </>
                );
              }}
            >
              <div className="field">
                <label htmlFor="ops-wipe-confirm">
                  Zur Bestätigung „{WIPE_CONFIRM_WORD}“ eingeben
                </label>
                <input
                  id="ops-wipe-confirm"
                  type="text"
                  value={wipeConfirm}
                  onChange={(event) => setWipeConfirm(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={!canSchema}
                  aria-describedby="ops-wipe-hint"
                />
                <span className="hint" id="ops-wipe-hint">
                  Erst dann wird die Schaltfläche freigegeben.
                </span>
              </div>
            </OperationCard>
          </div>
        </div>
      </div>
    </>
  );
}

export default SystemDataModelPage;
